import { useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useBubbleTooltip } from "./useBubbleTooltip";

/**
 * Global tooltip surface for bubble hover. Mounted exactly once at the
 * top of `AppLayout` so a single positioned card sits over every page —
 * mirrors the vanilla `#bubble-tooltip` singleton from `_legacy/xaes.css`.
 *
 * Visuals follow the hovered bubble's accent:
 *   - Border + accent rail tinted to the bubble's stroke colour
 *   - Title rendered in the accent
 *   - Body in the popover foreground token so it stays legible in
 *     both themes
 *   - Optional `badge` (small uppercase eyebrow) and `subtitle`
 *     (secondary line) — used by the HAW bubble tooltip per the
 *     richer 4-field layout from `_legacy/wd.html` #haw-bubble-tooltip
 *
 * Offset above-right of the cursor so it never overlaps the pointer
 * hit-target. Fades in/out via `motion/react` AnimatePresence.
 *
 * Viewport-edge clamping (ported from `_legacy/wd.html` lines 2609-2618):
 *   - Default placement is below-right of the cursor.
 *   - If the tooltip would overflow the right edge it shifts left so
 *     it sits flush 10 px inside the viewport.
 *   - If it would overflow the bottom edge it flips above the cursor.
 *   - Hard clamps at 10 px on every edge.
 * Measured against the live `getBoundingClientRect()` after the first
 * paint so variable-height tooltips (with/without badge/subtitle)
 * clamp correctly.
 */

const VIEWPORT_PAD = 10;
/** Fallback dimensions used on first render before the ref measures. */
const FALLBACK_W = 270;
const FALLBACK_H = 200;

export function BubbleTooltipOverlay() {
  const active = useBubbleTooltip((s) => s.active);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  // Cached size so subsequent positions can clamp synchronously
  // against measured dimensions rather than the fallback constants.
  const [size, setSize] = useState<{ w: number; h: number }>({
    w: FALLBACK_W,
    h: FALLBACK_H,
  });

  // After each render where the tooltip is mounted, remeasure so the
  // next position update uses the real card size (varies with
  // badge/subtitle presence and body length).
  useLayoutEffect(() => {
    if (!active || !tooltipRef.current) return;
    const rect = tooltipRef.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    if (rect.width === size.w && rect.height === size.h) return;
    setSize({ w: rect.width, h: rect.height });
  }, [active, size.w, size.h]);

  // Clamp position against the viewport using the cached card size.
  // Default placement is below-right of the cursor (legacy used
  // below-centred of the bubble; below-right of cursor is a closer
  // match to the existing React behaviour with the overflow guards
  // legacy provided).
  let left = FALLBACK_W; // sentinel — overwritten when active.
  let top = FALLBACK_H;
  if (active) {
    const TW = size.w;
    const TH = size.h;
    left = active.x + 16;
    top = active.y - 12;
    if (left + TW > window.innerWidth - VIEWPORT_PAD) {
      left = window.innerWidth - TW - VIEWPORT_PAD;
    }
    if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;
    if (top + TH > window.innerHeight - VIEWPORT_PAD) {
      top = active.y - TH - 12;
    }
    if (top < VIEWPORT_PAD) top = VIEWPORT_PAD;
  }

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key="bubble-tooltip"
          ref={tooltipRef}
          role="tooltip"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 4 }}
          transition={{ duration: 0.14, ease: "easeOut" }}
          style={{
            position: "fixed",
            top,
            left,
            maxWidth: 270,
            pointerEvents: "none",
            zIndex: 99999,
            borderColor: active.accent ?? "hsl(var(--border))",
            // Layered shadow: vanilla drop shadow (theme-aware via
            // --shadow-bubble-tooltip) + accent 1px ring + soft accent
            // halo.
            boxShadow: active.accent
              ? `var(--shadow-bubble-tooltip), 0 0 0 1px ${active.accent}, 0 0 18px ${active.accent}26`
              : "var(--shadow-bubble-tooltip)",
          }}
          className="bg-popover text-popover-foreground rounded-[10px] border px-3.5 py-3"
        >
          {/* Accent rail on the left edge */}
          <span
            aria-hidden
            className="absolute top-2 bottom-2 left-0 w-[3px] rounded-r"
            style={{ background: active.accent ?? "hsl(var(--primary))" }}
          />
          {active.badge && (
            <div
              className="font-sans text-fs-label font-bold tracking-[0.18em] uppercase opacity-65"
              style={{ color: active.accent ?? "hsl(var(--primary))" }}
            >
              {active.badge}
            </div>
          )}
          <div
            className="font-sans text-fs-label font-bold tracking-[0.12em] uppercase"
            style={{ color: active.accent ?? "hsl(var(--primary))" }}
          >
            {active.title}
          </div>
          {active.subtitle && (
            <div className="text-popover-foreground/70 mt-0.5 font-sans text-fs-label leading-snug">
              {active.subtitle}
            </div>
          )}
          {active.body &&
            (Array.isArray(active.body) ? (
              <ul className="text-popover-foreground/85 mt-1.5 flex flex-col gap-1 font-sans text-xs leading-relaxed">
                {active.body.map((line, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span aria-hidden style={{ color: active.accent }}>
                      •
                    </span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-popover-foreground/85 mt-1.5 font-sans text-xs leading-relaxed">
                {active.body}
              </div>
            ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
